import React, { useCallback, useEffect, useState, useRef } from 'react'
import { LatLngBounds, LatLngExpression, Map as LeafletMap } from 'leaflet'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Container from '@mui/material/Container'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import Tab from '@mui/material/Tab'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import ToggleButton from '@mui/material/ToggleButton'
import Typography from '@mui/material/Typography'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ErrorBox from '@/components/shared/boxes/ErrorBox'
import {
  DeleteRoute,
  LoadAccountMemberList, LoadFormList, LoadProjectList, LoadResourcePlaceList, LoadRouteList, LoadUserLocationList,
  LoadWorkFinishOptionList, LoadWorkLocationList
} from '@/javascript/domain/usecases'
import HeaderBox from '@/javascript/presentation/components/boxes/header-box'
import DateInput from '@/javascript/presentation/components/inputs/date-input'
import { getBoundsFromPoints } from '@/javascript/presentation/helpers'
import { MapBox, EmptyBox, Filters } from './components'
import { RouteItem } from '@/javascript/domain/entities/maps/route-item'
import RoutesBox from './components/routes-box/routes-box'
import './work-user-maps-styles.scss'
import Paywall from './components/Paywall'
import { UserLocation } from '@/javascript/domain/entities/maps/user-location'
import { WorkLocation } from '@/javascript/domain/entities/maps/work-location'

export type FetchLocationsParams = {
  date?: Date
  viewMode?: string[]
  searchText?: string
  resourcePlaces?: string[]
  forms?: string[]
  projects?: string[]
  providers?: string[]
  inspectors?: string[]
  status?: string
  finishOptions?: string[]
}

type Props = {
  loadAccountMemberList: LoadAccountMemberList
  loadFormList: LoadFormList
  loadProjectList: LoadProjectList
  loadResourcePlaces: LoadResourcePlaceList
  loadUserLocationList: LoadUserLocationList
  loadWorkFinishOptionList: LoadWorkFinishOptionList
  loadWorkLocationList: LoadWorkLocationList
  loadRouteList: LoadRouteList
  deleteRoute: DeleteRoute
  canCreateRoute: boolean
  isWorkTeamRoutingEnabled: boolean
  pollInterval?: number
}

const WorkUserMaps: React.FC<Props> = ({
  loadAccountMemberList, loadFormList, loadProjectList, loadResourcePlaces, loadUserLocationList,
  loadWorkFinishOptionList, loadWorkLocationList, loadRouteList, deleteRoute, canCreateRoute, isWorkTeamRoutingEnabled, pollInterval = 30000
}) => {
  const [locations, setLocations] = useState({
    works: [] as WorkLocation[],
    users: [] as UserLocation[],
    center: [0.0, 0.0] as LatLngExpression,
    bounds: null as LatLngBounds | null
  })
  const [isReady, setIsReady] = useState(false)
  const [isError, setIsError] = useState(false)
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const tab = params.get('tab')
    return tab !== null ? parseInt(tab) : 0
  })
  const [date, setDate] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const dateParam = params.get('date')
    return dateParam ? dayjs(dateParam) : dayjs()
  })
  const [routes, setRoutes] = useState<RouteItem[]>([])
  const [isRoutesReady, setIsRoutesReady] = useState(false)
  const [toggleFilter, setToggleFilter] = useState(false)
  const [routeBounds, setRouteBounds] = useState<LatLngBounds | null>(null)
  const lastLoadedRouteDate = useRef<string | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastFetchParamsRef = useRef<FetchLocationsParams>({})
  const hasInitiallyFitRef = useRef(false)

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const handleTab = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const mapRef = useRef<LeafletMap>(null)
  const routeMapRef = useRef<LeafletMap>(null)

  useEffect(() => {
    if (!isReady || locations.works.length === 0 && locations.users.length === 0) return
    const allLocations = [...locations.works, ...locations.users]
    const points = allLocations.map(l => [l.latitude, l.longitude] as [number, number])
    const bounds = getBoundsFromPoints(points)
    if (bounds) setLocations(old => ({ ...old, bounds, center: bounds.getCenter() }))
  }, [locations.users, locations.works])

  useEffect(() => {
    if (mapRef.current && locations.bounds && !hasInitiallyFitRef.current) {
      hasInitiallyFitRef.current = true
      mapRef.current.fitBounds(locations.bounds)
    }
  }, [locations.bounds])

  useEffect(() => {
    if (activeTab !== 1) return
    const dateKey = date.format('YYYY-MM-DD')
    if (dateKey === lastLoadedRouteDate.current) return
    lastLoadedRouteDate.current = dateKey
    setIsRoutesReady(false)
    loadRouteList.load({ date: date.toDate() }).then(setRoutes).finally(() => setIsRoutesReady(true))
  }, [activeTab, date])

  useEffect(() => {
    const members = routes.flatMap(r => r.members)
    if (members.length === 0) return
    const points = members.flatMap(m => m.points.map(p => [p.location.lat, p.location.lng] as [number, number]))
    setRouteBounds(getBoundsFromPoints(points))
  }, [routes])

  useEffect(() => {
    if (routeMapRef.current && routeBounds) routeMapRef.current.fitBounds(routeBounds, { padding: [40, 40] })
  }, [routeBounds])

  const loadLocations = useCallback(async (params: FetchLocationsParams): Promise<[UserLocation[], WorkLocation[]]> => {
    const recentDate = dayjs(params?.date).isSame(dayjs(), 'date')
    const resolveUsers = (!params?.viewMode || params.viewMode.includes("users")) && recentDate
    const resolveWorks = !params?.viewMode || params.viewMode.includes("works")
    return Promise.all([
      resolveUsers ? loadUserLocationList.load(params) : Promise.resolve([]),
      resolveWorks ? loadWorkLocationList.load(params) : Promise.resolve([])
    ])
  }, [loadUserLocationList, loadWorkLocationList])

  const fetchLocations = async (params?: FetchLocationsParams) => {
    lastFetchParamsRef.current = params ?? {}
    hasInitiallyFitRef.current = false
    setIsReady(false)
    setIsError(false)
    const [users, works] = await loadLocations(params ?? {})
      .catch((): [UserLocation[], WorkLocation[]] => {
        setIsError(true)
        return [[], []]
      })
    setIsReady(true)
    setLocations(prev => ({ ...prev, works, users }))
  }

  const refreshLocations = useCallback(async () => {
    const [users, works] = await loadLocations(lastFetchParamsRef.current)
      .catch((): [UserLocation[], WorkLocation[]] => [[], []])
    setLocations(prev => ({ ...prev, works, users }))
  }, [loadLocations])

  useEffect(() => {
    stopPolling()
    const isToday = date.isSame(dayjs(), 'date')
    if (activeTab === 0 && isToday) {
      pollIntervalRef.current = setInterval(refreshLocations, pollInterval)
    }
    return () => stopPolling()
  }, [activeTab, date, refreshLocations])

  return (
    <TabContext value={activeTab}>
      <Box sx={{ bgcolor: 'background.header' }}>
        <HeaderBox sx={{ paddingBottom: toggleFilter ? '1.5rem' : 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='h3'>Mapa</Typography>
            <Box>
              <Chip label='Beta' size='medium' sx={{ fontSize: '14px' }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
            <DateInput value={date} setValue={setDate} />
            {activeTab == 0 &&
              <ToggleButton
                value="check"
                sx={{ border: 'none', borderRadius: '50%' }}
                selected={toggleFilter}
                onChange={() => setToggleFilter(!toggleFilter)}
                data-testid="filter-toggle"
              >
                <FilterAltIcon />
              </ToggleButton>
            }
          </Box>
          <Box>
            <TabList onChange={handleTab} centered>
              <Tab label="Atividades" sx={{ minWidth: '15vw'}} value={0} />
              <Tab label="Rotas" sx={{ minWidth: '15vw'}} value={1} data-testid="route-tab" />
            </TabList>
            <Divider />
          </Box>
          <Collapse in={toggleFilter} data-testid="filter-collapse">
            <TabPanel value={0} sx={{ padding: 0 }} keepMounted>
              <Filters
                date={date}
                setDate={setDate}
                fetchLocations={fetchLocations}
                fetchAccountMembers={loadAccountMemberList}
                fetchForms={loadFormList}
                fetchProjects={loadProjectList}
                fetchResourcePlaces={loadResourcePlaces}
                fetchWorkFinishOptions={loadWorkFinishOptionList}
                isReady={isReady}
                isActive={activeTab === 0}
              />
            </TabPanel>
          </Collapse>
        </HeaderBox>
      </Box>
      <TabPanel value={0} sx={{ padding: 0 }}>
        <Box
          sx={{
            display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF',
            minHeight: '50vh', alignItems: 'center', justifyContent: 'center'
          }} data-testid="map-content-box">
            {!isReady &&
              <Container
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}
                data-testid="loading-box">
                <CircularProgress />
              </Container>
            }
            {isError &&
            <Container sx={{ marginBottom: '16px' }}>
              <ErrorBox
                errorMessage="Ocorreu um problema ao buscar as informações do mapa. Clique novamente em 'Aplicar filtros' ou recarregue a página."
                onClose={() => setIsError(false)}
                title="Erro ao carregar os dados: Ocorreu um problema ao buscar as informações do mapa."
              />
            </Container>
            }
            {isReady &&
              (
                locations.works.length === 0 && locations.users.length === 0
                  ? <EmptyBox />
                  : <MapBox ref={mapRef} locations={locations} />
              )
            }
        </Box>
      </TabPanel>
      <TabPanel value={1} sx={{ padding: 0, flex: 1 }}>
        { isWorkTeamRoutingEnabled ? (
          <Box
          sx={{
            display: 'flex', flexDirection: 'column', backgroundColor: '#FFFFFF',
            minHeight: '50vh', alignItems: 'center', justifyContent: 'center'
          }} data-testid="route-content-box">
            <RoutesBox
              isReady={isRoutesReady}
              routes={routes}
              setRoutes={setRoutes}
              ref={routeMapRef}
              deleteRoute={deleteRoute}
              canCreateRoute={canCreateRoute}
              date={date}
            />
        </Box>
        ):(
          <Paywall></Paywall>
        )}
      </TabPanel>
    </TabContext>
  )
}

export default WorkUserMaps
